import React from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import App, { preloadRouteForPath } from './App';
import './styles.css';

// Run a non-critical task once the browser is idle, with a load-event fallback for
// browsers without requestIdleCallback.
const whenIdle = (task: () => void, timeout: number, fallbackDelay: number): void => {
  const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void };
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(task, { timeout });
  } else {
    window.addEventListener('load', () => setTimeout(task, fallbackDelay));
  }
};

// Load Firebase (analytics only) as a separate chunk, off the critical render path.
const loadFirebase = (): void => {
  import('./firebase')
    .then((m) => m.initAnalytics())
    .catch(() => {});
};
if (typeof window !== 'undefined') {
  whenIdle(loadFirebase, 5000, 2000);
}

// Google AdSense (auto ads). This MUST NOT be a plain <script async> in index.html:
// auto ads inject <ins> containers into the page, and when that landed inside a
// React-managed container while hydrateRoot was still running, React threw
// #418/#423 and fell back to client-rendering the whole page — throwing away the
// prerendered HTML on roughly 1 in 6 cold homepage loads. Injecting it only after
// the root render has committed removes the race. Skipped entirely under react-snap
// so no ad markup can ever reach the prerendered snapshots.
const ADSENSE_SRC =
  'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6198019355502080';

const loadAdSense = (): void => {
  if (navigator.userAgent.includes('ReactSnap')) return;
  if (document.querySelector('script[data-adsense]')) return;

  const script = document.createElement('script');
  script.src = ADSENSE_SRC;
  script.async = true;
  script.crossOrigin = 'anonymous';
  script.setAttribute('data-adsense', '');
  document.head.appendChild(script);
};

// Register service worker for PWA functionality
const registerServiceWorker = (): void => {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker
        .register('/service-worker.js')
        .then((registration: ServiceWorkerRegistration) => {
          console.log('ServiceWorker registered:', registration.scope);

          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New content available, notify user if needed
                  console.log('New content available, refresh to update.');
                }
              });
            }
          });
        })
        .catch((error: Error) => {
          console.log('ServiceWorker registration failed:', error);
        });
    });
  }
};

// Initialize app with hydration support for react-snap pre-rendering
const rootElement = document.getElementById('root');
if (rootElement) {
  const app = (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );

  // During react-snap's prerender pass (puppeteer, userAgent === 'ReactSnap') we
  // always render fresh with createRoot — there is no hydration step, so the
  // route-split Suspense boundaries can resolve their chunks and be snapshotted
  // without any client/server mismatch. (react-snap aborts the async chunk fetch
  // a hydrateRoot would need, which is why hydrating during prerender failed.)
  const isPrerender =
    typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap');

  // The 11 multilingual templates contain many translated mixed-text nodes (and a
  // grouped preview <select>) that react-snap serializes merged; hydrating them would
  // mismatch. We client-render those pages fresh instead — the prerendered HTML still
  // serves SEO and first paint. All other (English) routes hydrate cleanly.
  const LANG_PATHS = new Set(['/es', '/pt', '/fr', '/de', '/id', '/ph', '/th', '/vi', '/jp', '/kr', '/cn']);
  const langPage = LANG_PATHS.has(window.location.pathname.replace(/\/+$/, '') || '/');

  if (rootElement.hasChildNodes() && !isPrerender) {
    // Preload the matched route's chunk so it renders synchronously on the first pass
    // (so a hydrated route matches the prerendered DOM, and a client-rendered language
    // page swaps in without a Suspense fallback flash).
    preloadRouteForPath(window.location.pathname)
      .catch(() => undefined)
      .then(() => {
        if (langPage) {
          createRoot(rootElement).render(app);
        } else {
          hydrateRoot(rootElement, app);
        }
        // Only once the root render has been committed — see loadAdSense above.
        whenIdle(loadAdSense, 3000, 1000);
      });
  } else {
    createRoot(rootElement).render(app);
    whenIdle(loadAdSense, 3000, 1000);
  }
}

// Register service worker
registerServiceWorker();
